/**
 * Entry Choice Component
 *
 * Account creation form for the onboarding entry step.
 * Shows email + password + confirm password fields with a
 * "Log me in" link below for existing users.
 *
 * @see {@link ./__tests__/entry-choice.test.tsx}
 */

import { Form } from 'react-router'
import { Button } from '@bnsn/ui/components/button'
import { Input } from '@bnsn/ui/components/input'
import { Label } from '@bnsn/ui/components/label'
import { Loader2 } from 'lucide-react'

import { SlideScreen } from '../../premium-blueprints/components/slide-screen'
import { ErrorBanner } from '../../premium-blueprints/components/error-banner'
import { ONBOARD_COPY } from '../lib/constants'

interface EntryChoiceProps {
  actionError?: string
  isSubmitting: boolean
  csrfToken: string
}

/**
 * Entry choice UI with create account form and a login link below.
 */
export const EntryChoice = ({ actionError, isSubmitting, csrfToken }: EntryChoiceProps): JSX.Element => {
  return (
    <SlideScreen headline={ONBOARD_COPY.entrySubheading} showBack={false} showContinue={false} footer={null} centerHeadline>
      <div>
        <img
          src="/images/jon-olya1b.webp"
          alt="Jon and Olya Benson"
          className="w-full md:w-1/2 md:float-left md:mr-6 mb-4 rounded-lg object-cover"
        />
        <p className="text-gray-600 whitespace-pre-line">{ONBOARD_COPY.entryBody}</p>
      </div>

      <ErrorBanner error={actionError} />

      {/* Create Account Form */}
      <Form method="post">
        <input type="hidden" name="csrfToken" value={csrfToken} />
        {/* Honeypot — hidden from real users, bots fill it in */}
        <div aria-hidden="true" className="absolute -left-[9999px]">
          <label htmlFor="website">Website</label>
          <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="create-email">Your Best Email</Label>
            <Input
              id="create-email"
              name="email"
              type="email"
              placeholder="you@example.com"
              required
              disabled={isSubmitting}
              autoComplete="email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-password">Create Your Password</Label>
            <Input
              id="create-password"
              name="password"
              type="password"
              placeholder="Create a password"
              required
              disabled={isSubmitting}
              autoComplete="new-password"
              minLength={8}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-confirm-password">Confirm Your Password</Label>
            <Input
              id="create-confirm-password"
              name="confirmPassword"
              type="password"
              placeholder="Confirm your password"
              required
              disabled={isSubmitting}
              autoComplete="new-password"
              minLength={8}
            />
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating account...
              </>
            ) : (
              ONBOARD_COPY.entryButtonText
            )}
          </Button>
        </div>
      </Form>

      {/* Login link for existing users */}
      <p className="text-center text-sm text-gray-500 mt-4">
        I have a BNSN account &mdash;{' '}
        <a href="/auth/sign-in/?returnUrl=/onboard" className="text-blue-600 hover:text-blue-700 underline">
          Log me in
        </a>
      </p>
    </SlideScreen>
  )
}
